/**
 * AI CONTEXT: security - Real authentication context with AWS Cognito
 * Journal: J-20250809-01
 * 
 * This context provides authentication state management with:
 * - Real AWS Cognito integration
 * - Secure session management
 * - Age verification for legal compliance
 * - Mobile-first authentication flow
 * - Proper error handling and user feedback
 * 
 * Architecture decisions:
 * - Use AWS Cognito for enterprise-grade security
 * - Implement proper loading states for better UX
 * - Handle authentication state changes consistently
 * - Integrate with existing context providers
 * 
 * Future considerations:
 * - Add biometric authentication for mobile
 * - Implement social login integration
 * - Add multi-factor authentication (MFA)
 * - Consider offline authentication support
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // AI CONTEXT: security - Check authentication status on app load
  // Reasoning: Determine if user is already authenticated when app starts
  // Constraints: Must handle loading states and errors gracefully
  // Future: Consider implementing token refresh logic

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setLoading(true);
        console.log('🔐 AuthContext: Starting authentication status check...');
        
        const { isAuthenticated, user } = await authService.checkAuthStatus();
        console.log('🔐 AuthContext: Auth status result:', { isAuthenticated, hasUser: !!user });
        
        if (isAuthenticated && user) {
          console.log('🔐 AuthContext: User authenticated, setting user state');
          setUser(user);
        } else {
          console.log('🔐 AuthContext: User not authenticated, clearing user state');
          setUser(null);
        }
      } catch (error) {
        console.error('🔐 AuthContext: Auth status check failed:', error);
        setUser(null);
      } finally {
        setLoading(false);
        setAuthChecked(true);
        console.log('🔐 AuthContext: Authentication check completed, authChecked:', true);
      }
    };

    checkAuthStatus();
  }, []);

  // AI CONTEXT: security - User registration with age verification
  // Reasoning: Collect necessary user information for legal compliance
  // Constraints: Must verify age and collect required information
  // Future: Consider additional verification methods

  const register = async (userData) => {
    try {
      setLoading(true);
      const result = await authService.register(userData);
      
      if (result.success) {
        // Don't set user yet - they need to confirm their email
        return { success: true, requiresConfirmation: true };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'REGISTRATION_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Account confirmation
  // Reasoning: Email verification required for security and legal compliance
  // Constraints: Must handle verification code properly
  // Future: Consider SMS verification as alternative

  const confirmAccount = async (email, code) => {
    try {
      setLoading(true);
      const result = await authService.confirmAccount(email, code);
      
      if (result.success) {
        // User is now confirmed but not logged in
        return { success: true };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Account confirmation error:', error);
      return { success: false, error: 'CONFIRMATION_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Confirm account and automatically sign in user
  // Reasoning: After email confirmation, automatically sign in user to enable profile data saving
  // Constraints: Must handle both confirmation and sign-in steps
  // Future: Consider implementing remember me functionality

  const confirmAccountAndSignIn = async (email, code, password) => {
    try {
      setLoading(true);
      const result = await authService.confirmAccountAndSignIn(email, code, password);
      
      if (result.success) {
        // If user was successfully signed in, update the user state
        if (!result.requiresSignIn) {
          const userDetails = await authService.getCurrentUserDetails();
          if (userDetails) {
            setUser(userDetails);
          }
        }
        return result;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Account confirmation and sign-in error:', error);
      return { success: false, error: 'CONFIRMATION_SIGNIN_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Resend confirmation code
  // Reasoning: Users may not receive or lose confirmation emails
  // Constraints: Rate limiting applies
  // Future: Consider SMS as backup verification method

  const resendConfirmationCode = async (email) => {
    try {
      setLoading(true);
      const result = await authService.resendConfirmationCode(email);
      return result;
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { success: false, error: 'RESEND_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - User login with session management
  // Reasoning: Secure authentication with proper session handling
  // Constraints: Must handle various login scenarios
  // Future: Consider implementing "remember me" functionality

  const login = async (email, password) => {
    try {
      setLoading(true);
      console.log('🔐 AuthContext: Login attempt started for:', email);
      
      const result = await authService.login(email, password);
      console.log('🔐 AuthContext: Login result:', result);
      
      if (result.success && result.user) {
        console.log('🔐 AuthContext: Login successful, setting user state');
        setUser(result.user);
        return { success: true, user: result.user };
      } else {
        console.log('🔐 AuthContext: Login failed:', result);
        return result;
      }
    } catch (error) {
      console.error('🔐 AuthContext: Login error:', error);
      return { success: false, error: 'LOGIN_FAILED' };
    } finally {
      setLoading(false);
      console.log('🔐 AuthContext: Login attempt completed');
    }
  };

  // AI CONTEXT: security - Secure logout
  // Reasoning: Properly clear all authentication data
  // Constraints: Must clear local storage and session data
  // Future: Consider implementing "logout from all devices"

  const logout = async () => {
    try {
      setLoading(true);
      const result = await authService.logout();
      
      if (result.success) {
        setUser(null);
        return { success: true };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, clear the user state
      setUser(null);
      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Password reset functionality
  // Reasoning: Users need ability to reset forgotten passwords
  // Constraints: Must follow AWS Cognito password reset flow
  // Future: Consider implementing passwordless authentication

  const resetPassword = async (email) => {
    try {
      setLoading(true);
      const result = await authService.resetPassword(email);
      return result;
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: 'RESET_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Confirm password reset
  // Reasoning: Complete password reset process securely
  // Constraints: Must verify reset code and new password
  // Future: Consider implementing password strength requirements

  const confirmResetPassword = async (email, code, newPassword) => {
    try {
      setLoading(true);
      const result = await authService.confirmResetPassword(email, code, newPassword);
      return result;
    } catch (error) {
      console.error('Confirm password reset error:', error);
      return { success: false, error: 'CONFIRM_RESET_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Update user profile
  // Reasoning: Allow users to update their profile information
  // Constraints: Must handle AWS Cognito attribute updates
  // Future: Consider implementing profile picture upload

  const updateUser = async (updates) => {
    try {
      setLoading(true);
      const result = await authService.updateUserAttributes(updates);
      
      if (result.success) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        return { success: true };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: 'UPDATE_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: business-logic - Add purchase to user profile
  // Reasoning: Track user purchases for credit system and legal compliance
  // Constraints: Must update both local state and backend
  // Future: Consider implementing real-time sync with backend

  const addPurchase = async (purchase) => {
    try {
      setLoading(true);
      
      // Update local state immediately for better UX
      const updatedUser = {
        ...user,
        purchases: [...user.purchases, purchase],
        entries: user.entries + purchase.entries
      };
      setUser(updatedUser);
      
      // TODO: Update backend through API
      // const result = await apiService.updateUserPurchases(user.id, updatedUser.purchases);
      
      return { success: true };
    } catch (error) {
      console.error('Add purchase error:', error);
      return { success: false, error: 'PURCHASE_UPDATE_FAILED' };
    } finally {
      setLoading(false);
    }
  };

  // AI CONTEXT: security - Check if user can access firearm giveaways
  // Reasoning: Utah sweepstakes laws require 21+ verification for firearm giveaways
  // Constraints: Must verify age before allowing access
  // Future: Consider additional verification methods

  const canAccessFirearmGiveaways = () => {
    if (!user) return false;
    return user.age >= 21;
  };

  const value = {
    user,
    loading,
    authChecked,
    login,
    register,
    logout,
    confirmAccount,
    confirmAccountAndSignIn,
    resendConfirmationCode,
    resetPassword,
    confirmResetPassword,
    updateUser,
    addPurchase,
    canAccessFirearmGiveaways,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    isSubscriber: user?.isSubscriber || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 